import type { SceneBlock } from './script-parser';
import {
  ALL_CAMERA_MOVEMENTS,
  ALL_EFFECT_TYPES,
  ALL_TYPOGRAPHY_ANIMATIONS,
  ALL_TYPOGRAPHY_STYLES,
  type CameraMovement,
  type ShotEffect,
  type ShotEffectType,
  type ShotPlan,
  type TypographyAnimation,
  type TypographyStyle,
  VALID_EMOTIONS,
} from './shot-planner';

export type LlmProviderId = 'openai' | 'anthropic' | 'openrouter';

export interface LlmConfig {
  provider: LlmProviderId;
  apiKey: string;
  model: string;
}

export interface LlmProviderInfo {
  label: string;
  defaultModel: string;
  endpoint: string;
  apiKeyHint: string;
}

export const LLM_PROVIDERS: Record<LlmProviderId, LlmProviderInfo> = {
  openai: {
    label: 'OpenAI',
    defaultModel: 'gpt-4o-mini',
    endpoint: 'https://api.openai.com/v1/chat/completions',
    apiKeyHint: 'sk-...',
  },
  anthropic: {
    label: 'Anthropic',
    defaultModel: 'claude-3-5-haiku-latest',
    endpoint: 'https://api.anthropic.com/v1/messages',
    apiKeyHint: 'sk-ant-...',
  },
  openrouter: {
    label: 'OpenRouter',
    defaultModel: 'meta-llama/llama-3.3-70b-instruct',
    endpoint: 'https://openrouter.ai/api/v1/chat/completions',
    apiKeyHint: 'sk-or-...',
  },
};

export interface LlmRequestSpec {
  url: string;
  headers: Record<string, string>;
  body: unknown;
}

const SYSTEM_PROMPT = `Você é um diretor de cinema especializado em dirigir roteiros.
Receba as cenas de um roteiro e produza a direção de cada uma como JSON válido.
Use APENAS os valores permitidos. Responda SOMENTE com o JSON, sem markdown.

Formato esperado:
{
  "shots": [
    {
      "title": "Título curto da cena",
      "subtitle": "Uma linha de diálogo ou ação",
      "purpose": "Intenção narrativa do plano",
      "emotion": "suspense | joy | contemplation | wonder",
      "energy": 0.0 a 1.0,
      "cameraMovement": "slow_push | slow_pull | dolly | truck | orbit | crane | tilt | pan | rack_focus | reveal | handheld | static",
      "effects": [ { "type": "color-grade | grain | vignette | dof | bloom | blur | chromatic-aberration | halation | sharpen", "intensity": 0.0 a 1.0 } ],
      "typography": [ { "text": "Texto do título", "style": "title | subtitle | chapter | quote | statistic | lower-third | caption | credit", "animation": "fade | tracking | mask-reveal | word-reveal | character-reveal | scale | blur | clip", "animationDuration": 1.5, "delay": 0 } ]
    }
  ]
}
Direções: 1 shot por cena, na mesma ordem. Título em maiúsculas. Efeitos coerentes com a emoção.`;

export function buildLlmPrompt(scenes: SceneBlock[]): string {
  const userContent = scenes
    .map(
      (scene) => `CENA ${scene.id}: ${scene.title}\n${scene.lines.map((l) => l.text).join('\n')}`
    )
    .join('\n\n');
  return userContent;
}

export function buildLlmRequest(config: LlmConfig, scenes: SceneBlock[]): LlmRequestSpec {
  const provider = LLM_PROVIDERS[config.provider];
  const userContent = buildLlmPrompt(scenes);

  if (config.provider === 'anthropic') {
    return {
      url: provider.endpoint,
      headers: {
        'content-type': 'application/json',
        'x-api-key': config.apiKey,
        'anthropic-version': '2023-06-01',
      },
      body: {
        model: config.model,
        max_tokens: 2000,
        system: SYSTEM_PROMPT,
        messages: [{ role: 'user', content: userContent }],
      },
    };
  }

  return {
    url: provider.endpoint,
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${config.apiKey}`,
    },
    body: {
      model: config.model,
      temperature: 0.7,
      response_format: { type: 'json_object' },
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: userContent },
      ],
    },
  };
}

export function extractShotsFromResponse(text: string): unknown[] {
  const trimmed = text.trim();
  const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/);
  const jsonText = fenced ? fenced[1] : trimmed;
  const parsed = JSON.parse(jsonText) as { shots?: unknown };
  if (!Array.isArray(parsed.shots)) {
    throw new Error('Resposta do LLM sem o campo "shots"');
  }
  return parsed.shots;
}

export function sanitizeShotPlan(
  raw: unknown,
  sceneId: string,
  shotId: string,
  index: number
): ShotPlan {
  const value = (raw ?? {}) as Record<string, unknown>;
  const fallbackMovement: CameraMovement = 'static';
  const fallbackEffect: ShotEffect = { type: 'color-grade', intensity: 0.5 };

  const emotion = VALID_EMOTIONS.includes(value.emotion as never)
    ? (value.emotion as ShotPlan['emotion'])
    : 'wonder';

  const energy = clampNumber(value.energy, 0.15, 1, 0.45);
  const cameraMovement = ALL_CAMERA_MOVEMENTS.includes(value.cameraMovement as never)
    ? (value.cameraMovement as CameraMovement)
    : fallbackMovement;

  const effects = Array.isArray(value.effects)
    ? (value.effects as unknown[])
        .filter((effect): effect is Record<string, unknown> => Boolean(effect))
        .filter((effect) => ALL_EFFECT_TYPES.includes(effect.type as never))
        .map((effect) => ({
          type: effect.type as ShotEffectType,
          intensity: clampNumber(effect.intensity, 0, 1, 0.5),
        }))
        .slice(0, 6)
    : [];

  if (effects.length === 0) effects.push(fallbackEffect);

  const rawTypography = Array.isArray(value.typography) ? value.typography[0] : value.typography;
  const typographyRaw = (rawTypography ?? {}) as Record<string, unknown>;
  const typography = [
    {
      text:
        typeof typographyRaw.text === 'string' && typographyRaw.text.length > 0
          ? typographyRaw.text
          : `Cena ${index + 1}`,
      style: ALL_TYPOGRAPHY_STYLES.includes(typographyRaw.style as never)
        ? (typographyRaw.style as TypographyStyle)
        : 'title',
      animation: ALL_TYPOGRAPHY_ANIMATIONS.includes(typographyRaw.animation as never)
        ? (typographyRaw.animation as TypographyAnimation)
        : 'mask-reveal',
      animationDuration: clampNumber(typographyRaw.animationDuration, 0.1, 10, 1.5),
      delay: clampNumber(typographyRaw.delay, 0, 60, 0),
    },
  ];

  return {
    sceneId,
    shotId,
    title: typeof value.title === 'string' ? value.title : `Cena ${index + 1}`,
    subtitle: typeof value.subtitle === 'string' ? value.subtitle : '',
    purpose: typeof value.purpose === 'string' ? value.purpose : 'Plano dirigido por IA',
    emotion,
    energy: Number(energy.toFixed(2)),
    cameraMovement,
    effects,
    typography,
  };
}

export async function generateDirectedShots(
  config: LlmConfig,
  scenes: SceneBlock[],
  signal?: AbortSignal
): Promise<ShotPlan[]> {
  if (!config.apiKey) throw new Error('API key não configurada');
  const request = buildLlmRequest(config, scenes);

  const response = await fetch(request.url, {
    method: 'POST',
    headers: request.headers,
    body: JSON.stringify(request.body),
    signal,
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    const message =
      response.status === 401
        ? 'API key inválida — verifique a chave no painel'
        : response.status === 404
          ? `Modelo "${config.model}" não encontrado — verifique o nome no painel`
          : `Erro ${response.status} do provedor`;
    throw new Error(`${message}${detail ? ` (${detail.slice(0, 140)})` : ''}`);
  }

  const payload = (await response.json()) as {
    choices?: Array<{ message?: { content?: string } }>;
    content?: unknown;
  };
  const content =
    config.provider === 'anthropic'
      ? extractAnthropicContent(payload.content)
      : payload.choices?.[0]?.message?.content;

  if (!content) throw new Error('Resposta do LLM sem conteúdo');
  const rawShots = extractShotsFromResponse(content);
  if (rawShots.length === 0) throw new Error('O LLM não produziu planos');

  return rawShots.map((raw, index) => {
    const scene = scenes[Math.min(index, scenes.length - 1)];
    return sanitizeShotPlan(raw, scene.id, `shot-${scene.id}`, index);
  });
}

function extractAnthropicContent(content: unknown): string | undefined {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    const block = content.find((item) => item && typeof item.text === 'string');
    return block?.text;
  }
  return undefined;
}

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}
